import { allDatabaseColumnTypes, columnTypeLabel } from '@/lib/databases/values';
import { createElement, useEffect, useRef, useState } from 'react';

import { ColumnTypeIcon } from './ColumnTypeIcon.web';

export function ColumnTypePicker({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (type: string) => void;
  disabled?: boolean;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    function onPointerDown(event: MouseEvent): void {
      const target = event.target as HTMLElement | null;
      if (target?.closest('[data-cs-type-picker="true"]')) {
        return;
      }
      setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown, true);
    return () => document.removeEventListener('mousedown', onPointerDown, true);
  }, [open]);

  return createElement(
    'div',
    {
      ref: rootRef,
      'data-cs-type-picker': 'true',
      className: 'cs-db-type-picker',
    },
    createElement(
      'button',
      {
        type: 'button',
        className: 'cs-db-type-picker-trigger',
        disabled,
        'aria-expanded': open,
        'aria-haspopup': 'listbox',
        onClick: () => {
          if (!disabled) {
            setOpen((prev) => !prev);
          }
        },
      },
      createElement(
        'span',
        { className: 'cs-db-menu-type-label' },
        createElement(ColumnTypeIcon, { type: value }),
        columnTypeLabel(value),
      ),
      createElement(
        'svg',
        {
          xmlns: 'http://www.w3.org/2000/svg',
          viewBox: '0 0 16 16',
          fill: 'none',
          width: 14,
          height: 14,
          className: `cs-db-type-picker-chevron${open ? ' cs-db-type-picker-chevron-open' : ''}`,
          'aria-hidden': true,
        },
        createElement('path', {
          stroke: 'currentColor',
          strokeWidth: 1.5,
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
          d: 'M4.5 6.25 8 9.75l3.5-3.5',
        }),
      ),
    ),
    open
      ? createElement(
          'div',
          { className: 'cs-db-type-picker-menu', role: 'listbox' },
          allDatabaseColumnTypes().map((t) =>
            createElement(
              'button',
              {
                key: t,
                type: 'button',
                role: 'option',
                'aria-selected': t === value,
                className: `cs-db-type-picker-option${t === value ? ' cs-db-type-picker-option-selected' : ''}`,
                onClick: () => {
                  onChange(t);
                  setOpen(false);
                },
              },
              createElement(
                'span',
                { className: 'cs-db-menu-type-label' },
                createElement(ColumnTypeIcon, { type: t }),
                columnTypeLabel(t),
              ),
            ),
          ),
        )
      : null,
  );
}
